// Path: ./src/app/components/CustomAutocomplete/index.tsx
import { ChangeEvent, useEffect, useRef, useState } from "react";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import { CircularProgress, Stack, Tooltip } from "@mui/material";
import {
  Container,
  Wrapper,
  Title,
  SelectedOptions,
  SelectedOption,
  OptionText,
  Input,
  OptionsList,
  Option,
  UnselectButton,
  SelectedIcon,
} from "./styled";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import InfoIcon from "@mui/icons-material/Info";

export interface AutocompleteOption<T> {
  label: string;
  value: T;
}

interface Props<T> {
  title?: string;
  toolTipInfoText?: string;
  multiSelect?: boolean;
  optionsList?: AutocompleteOption<T>[];
  selectedValues?: AutocompleteOption<T>[];
  handleOnChange?: (value?: T[] | []) => void;
  WrapperCss?: string;
  inputCss?: string;
  titleCss?: string;
  inputTextCss?: string;
  selectListCss?: string;
  isLoading?: boolean;
  handleOnSearch?: (value: string) => void;
  dataTestId?: string;
  disabled?: boolean;
  allowSelectAll?: boolean; // New prop added here
}

export const CustomAutocompleteMultiSelect = <T,>({
  optionsList,
  selectedValues,
  multiSelect = true, //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) this is bad, need to create another component to handle single select, not using a flag
  handleOnChange,
  isLoading,
  title,
  toolTipInfoText,
  WrapperCss,
  titleCss,
  inputCss,
  handleOnSearch,
  dataTestId,
  inputTextCss,
  selectListCss,
  disabled = false, // default value is false
  allowSelectAll = false,
}: Props<T>) => {
  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<
    AutocompleteOption<T>[]
  >(selectedValues ? selectedValues : []);
  const [inputValue, setInputValue] = useState("");
  const [openDrop, setOpenDrop] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
    setOpenDrop(true);
    setHighlightedIndex(-1);
    handleOnSearch && handleOnSearch(value);
  };

  /**
   * Handles the click event on an option.
   * Adds or removes the clicked option based on the current selection mode.
   *
   * @param option - The option that was clicked.
   */
  const handleOptionClick = (option: AutocompleteOption<T>) => {
    if (
      !selectedOptions.find(
        (selectedOption) =>
          (selectedOption.value as any).id === (option.value as any).id //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) bad idea will come back
      )
    ) {
      if (multiSelect) {
        handleOnChange &&
          handleOnChange([
            ...selectedOptions.map((option) => {
              return option.value;
            }),
            option.value,
          ]);
        setSelectedOptions([...selectedOptions, option]);
      } else {
        handleOnChange && handleOnChange([option.value]);

        setSelectedOptions([option]);
      }

      setInputValue("");
    } else {
      handleRemoveOption(option);
    }
    setOpenDrop(false);
  };

  /**
   * Handles the removal of a specific option from the selected options list.
   *
   * @param option - The option to be removed.
   */
  const handleRemoveOption = (option: AutocompleteOption<T>) => {
    const updatedOptions = selectedOptions.filter((selectedOption) => {
      return (selectedOption.value as any).id !== (option.value as any).id; //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) bad idea will come back
    });
    setSelectedOptions(updatedOptions);
    handleOnChange &&
      handleOnChange(updatedOptions.map((option) => option.value));
  };

  /**
   * Handles clicks that occur outside the specified container.
   * closes the dropdown if the click is outside the container.
   *
   * @param event - The MouseEvent triggered by the click event.
   */
  const handleOutsideClick = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setInputValue("");
      setOpenDrop(false);
    }
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      handleOutsideClick(event);
    };

    window.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setSelectedOptions(selectedValues ? selectedValues : []);
  }, [selectedValues]);

  useEffect(() => {
    if (!openDrop) {
      setHighlightedIndex(-1);
    }
  }, [openDrop]);

  const filteredOptions = optionsList?.filter((option) =>
    option?.label?.toLowerCase()?.includes(inputValue?.toLowerCase())
  );

  const hasSelectAllOption =
    allowSelectAll &&
    !inputValue?.trim()?.length &&
    filteredOptions &&
    filteredOptions.length > 0;

  const selectAllOptionSelected = filteredOptions?.every((option) =>
    selectedOptions.some(
      (selectedOption) =>
        (selectedOption.value as any).id === (option.value as any).id
    )
  );

  const handleSelectAll = () => {
    if (selectAllOptionSelected) {
      const updatedOptions = selectedOptions.filter(
        (selectedOption) =>
          !filteredOptions?.some(
            (option) =>
              (selectedOption.value as any).id === (option.value as any).id
          )
      );
      setSelectedOptions(updatedOptions);
      handleOnChange &&
        handleOnChange(updatedOptions.map((option) => option.value));
    } else {
      const newOptions = filteredOptions?.filter(
        (option) =>
          !selectedOptions.some(
            (selectedOption) =>
              (selectedOption.value as any).id === (option.value as any).id
          )
      );
      const updatedOptions = [...selectedOptions, ...(newOptions || [])];
      setSelectedOptions(updatedOptions);
      handleOnChange &&
        handleOnChange(updatedOptions.map((option) => option.value));
    }
  };

  /**
   * Handles keyboard events for navigation and selection
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!openDrop) {
      // Only handle specific keys when dropdown is closed
      if (event.key === "ArrowDown" || event.key === "Enter") {
        event.preventDefault();
        setOpenDrop(true);
        setHighlightedIndex(0);
      }
      return;
    }

    const visibleOptions = [];

    if (hasSelectAllOption) {
      visibleOptions.push({ type: "selectAll", index: 0 });
    }

    filteredOptions?.forEach((option, index) => {
      visibleOptions.push({
        type: "option",
        index: hasSelectAllOption ? index + 1 : index,
        option,
      });
    });

    const totalOptions = visibleOptions.length;
    if (totalOptions === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        event.stopPropagation();
        setHighlightedIndex((prev) => {
          const newIndex =
            prev === -1 ? 0 : prev < totalOptions - 1 ? prev + 1 : 0;
          setTimeout(() => scrollToHighlighted(newIndex), 0);
          return newIndex;
        });
        break;
      case "ArrowUp":
        event.preventDefault();
        event.stopPropagation();
        setHighlightedIndex((prev) => {
          const newIndex =
            prev === -1
              ? totalOptions - 1
              : prev > 0
              ? prev - 1
              : totalOptions - 1;
          setTimeout(() => scrollToHighlighted(newIndex), 0);
          return newIndex;
        });
        break;
      case "Enter":
        event.preventDefault();
        event.stopPropagation();

        if (highlightedIndex === -1 && totalOptions > 0) {
          const firstIndex = 0;
          if (hasSelectAllOption && firstIndex === 0) {
            handleSelectAll();
          } else {
            const optionIndex = hasSelectAllOption
              ? firstIndex - 1
              : firstIndex;
            if (filteredOptions && filteredOptions[optionIndex]) {
              handleOptionClick(filteredOptions[optionIndex]);
            }
          }
        } else if (highlightedIndex >= 0) {
          if (hasSelectAllOption && highlightedIndex === 0) {
            handleSelectAll();
          } else {
            const optionIndex = hasSelectAllOption
              ? highlightedIndex - 1
              : highlightedIndex;
            if (filteredOptions && filteredOptions[optionIndex]) {
              handleOptionClick(filteredOptions[optionIndex]);
            }
          }
        }
        break;
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        setOpenDrop(false);
        setHighlightedIndex(-1);
        setInputValue("");
        break;
    }
  };

  /**
   * Scrolls the highlighted option into view
   */
  const scrollToHighlighted = (index: number) => {
    if (!containerRef.current) return;

    const optionsList = containerRef.current.querySelector(
      `[data-testid="${dataTestId}-optionsList"]`
    );
    if (!optionsList) return;

    const options = optionsList.querySelectorAll(
      `[data-testid="${dataTestId}-option"], [data-testid="${dataTestId}-selectAllOption"]`
    );
    const targetOption = options[index] as HTMLElement;

    if (targetOption) {
      targetOption.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  return (
    <Wrapper
      WrapperCss={WrapperCss}
      data-testid="customautocompletemultiselect-test-id"
    >
      {title ? (
        toolTipInfoText ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Title titleCss={titleCss}>{title}</Title>
            <Tooltip enterDelay={1000} title={toolTipInfoText} arrow>
              <InfoIcon
                sx={{ fontSize: 16, color: "#9ca3afab", cursor: "help" }}
              />
            </Tooltip>
          </Stack>
        ) : (
          <Title titleCss={titleCss}>{title}</Title>
        )
      ) : (
        <></>
      )}

      <Container ref={containerRef} onKeyDown={handleKeyDown} tabIndex={0}>
        <SelectedOptions
          openDrop={openDrop}
          inputCss={inputCss}
          data-testid={dataTestId}
          onClick={(e) => {
            if (e.target === e.currentTarget && !disabled) {
              setOpenDrop(true);
            }
          }}
        >
          {selectedOptions.map((option, index) => (
            <SelectedOption
              key={index}
              data-testid={`${dataTestId}-selectedOption`}
            >
              <OptionText>{option.label}</OptionText>
              <UnselectButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!disabled) handleRemoveOption(option);
                }}
                data-testid={`${dataTestId}-selectedOption-cancel-button`}
              >
                <ClearIcon sx={{ fontSize: 12 }} />
              </UnselectButton>
            </SelectedOption>
          ))}
          <Input
            type="text"
            inputTextCss={inputTextCss}
            value={inputValue}
            onChange={handleInputChange}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) {
                setOpenDrop(true);
              }
            }}
            onFocus={() => {
              if (!disabled) {
                setOpenDrop(true);
              }
            }}
            disabled={disabled}
            onKeyDown={handleKeyDown}
          />
          {isLoading ? (
            <CircularProgress sx={{ color: "#ff8038" }} size={22} />
          ) : (
            <></>
          )}
        </SelectedOptions>
        {openDrop && !disabled && (
          <OptionsList
            selectListCss={selectListCss}
            data-testid={`${dataTestId}-optionsList`}
          >
            {hasSelectAllOption && (
              <Option
                onClick={handleSelectAll}
                selected={selectAllOptionSelected}
                data-testid={`${dataTestId}-selectAllOption`}
                isSelectAllOption={true}
                highlighted={highlightedIndex === 0}
                onMouseEnter={() => setHighlightedIndex(0)}
              >
                <OptionText>
                  {selectAllOptionSelected
                    ? `${t(messages.UNSELECT_ALL())}`
                    : `${t(messages.SELECT_ALL())}`}
                </OptionText>
              </Option>
            )}
            {filteredOptions?.map((option, index) => {
              const actualIndex = hasSelectAllOption ? index + 1 : index;

              return (
                <Option
                  key={index}
                  onClick={() => handleOptionClick(option)}
                  selected={selectedOptions.some((selectedOption) => {
                    return (
                      //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) bad idea will come back
                      (selectedOption.value as any).id ===
                      (option.value as any).id
                    );
                  })}
                  highlighted={highlightedIndex === actualIndex}
                  data-testid={`${dataTestId}-option`}
                  onMouseEnter={() => setHighlightedIndex(actualIndex)}
                >
                  <OptionText>{option.label}</OptionText>
                  {selectedOptions.some((selectedOption) => {
                    return (
                      (selectedOption.value as any).id ===
                      (option.value as any).id
                    );
                  }) && (
                    <SelectedIcon
                      onClick={() => handleRemoveOption(option)}
                      selected={selectedOptions.some((selectedOption) => {
                        return (
                          //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) bad idea will come back
                          (selectedOption.value as any).id ===
                          (option.value as any).id
                        );
                      })}
                    >
                      <DoneIcon />
                    </SelectedIcon>
                  )}
                </Option>
              );
            })}
          </OptionsList>
        )}
      </Container>
    </Wrapper>
  );
};
